import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Divider,
  Paper,
  FormControlLabel,
  Checkbox,
  Chip,
  Avatar,
  IconButton,
  Tooltip
} from "@material-ui/core";
import {
  Save as SaveIcon,
  BookmarkBorder as BookmarkBorderIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  ArrowForward as ArrowForwardIcon,
  Message as MessageIcon,
  Today as TodayIcon,
  Label as LabelIcon,
  PriorityHigh as PriorityHighIcon
} from "@material-ui/icons";
import { format, parseISO } from "date-fns";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import useTicketCard from "../../hooks/useTicketCard";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import toastSuccess from "../../errors/toastSuccess";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2)
  },
  dialogContent: {
    paddingBottom: theme.spacing(2)
  },
  title: {
    fontWeight: "bold",
    marginBottom: theme.spacing(2)
  },
  formControl: {
    margin: theme.spacing(1, 0)
  },
  selectEmpty: {
    marginTop: theme.spacing(1)
  },
  priorityLow: {
    color: theme.palette.success.main
  },
  priorityMedium: {
    color: theme.palette.warning.main
  },
  priorityHigh: {
    color: theme.palette.error.main
  },
  tabPanel: {
    padding: theme.spacing(2, 0)
  },
  divider: {
    margin: theme.spacing(2, 0)
  },
  chip: {
    margin: theme.spacing(0.5)
  },
  messagePreview: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    backgroundColor: "#f5f5f5",
    borderRadius: theme.shape.borderRadius,
    maxHeight: "200px",
    overflowY: "auto"
  },
  messageText: {
    whiteSpace: "pre-wrap"
  },
  messageHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1)
  },
  messageTimestamp: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary
  },
  selectMessagesButton: {
    marginBottom: theme.spacing(1)
  },
  progress: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2)
  },
  messageCount: {
    marginLeft: theme.spacing(1)
  },
  finalStageInfo: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
    borderRadius: theme.shape.borderRadius,
    display: "flex",
    alignItems: "center"
  },
  icon: {
    marginRight: theme.spacing(1)
  }
}));

const FinalizeTicketModal = ({ open, onClose, onSave, ticket, loading }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const locale = i18n.language === "pt" ? ptBR : enUS;
  
  const { 
    card, 
    stages, 
    createCard, 
    updateCard, 
    moveCard, 
    loading: cardLoading 
  } = useTicketCard(ticket?.id);
  
  const [finalizing, setFinalizing] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "medium",
    tags: "",
    dueDate: null
  });
  const [finalStage, setFinalStage] = useState(null);
  const [includeMessagesInContext, setIncludeMessagesInContext] = useState(true);
  const [selectedMessages, setSelectedMessages] = useState([]);
  
  useEffect(() => {
    if (card) {
      setFormData({
        title: card.title || "",
        description: card.description || "",
        priority: card.priority || "medium",
        tags: card.tags || "",
        dueDate: card.dueDate ? new Date(card.dueDate) : null
      });
    } else if (ticket) {
      // Definir um título padrão se não houver cartão existente
      setFormData(prev => ({
        ...prev,
        title: `${ticket.contact?.name || "Sem nome"} - ${format(new Date(), "dd/MM/yyyy")}`
      }));
    }
  }, [card, ticket]);
  
  useEffect(() => {
    // Encontrar o estágio final configurado para a fila
    if (stages && stages.length > 0) {
      const finalStageFound = stages.find(stage => stage.isFinal);
      setFinalStage(finalStageFound);
    }
  }, [stages]);
  
  // Buscar as mensagens do ticket para seleção do contexto
  useEffect(() => {
    const fetchMessages = async () => {
      if (ticket?.id && includeMessagesInContext) {
        try {
          const { data } = await api.get(`/messages/${ticket.id}`);
          if (data.messages && Array.isArray(data.messages)) {
            // Pré-selecionar todas as mensagens
            setSelectedMessages(data.messages);
          }
        } catch (err) {
          console.error(err);
        }
      }
    };
    
    fetchMessages();
  }, [ticket, includeMessagesInContext]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleDateChange = (date) => {
    setFormData(prev => ({ ...prev, dueDate: date }));
  };
  
  const handleSave = async () => {
    setFinalizing(true);
    
    try {
      if (!card) {
        // Se não houver cartão, criar um novo
        await createCard(ticket.queueId, user?.id);
      }
      
      // Atualizar as informações do cartão
      await updateCard(formData);
      
      // Se houver um estágio final configurado, mover o cartão para ele
      if (finalStage) {
        await moveCard(finalStage.id);
      }
      
      // Adicionar mensagens selecionadas ao contexto
      if (includeMessagesInContext && selectedMessages.length > 0) {
        for (const message of selectedMessages) {
          try {
            await api.post(`/kanban/cards/${card.id}/messages`, {
              messageId: message.id
            });
          } catch (err) {
            console.error(`Erro ao adicionar mensagem ${message.id} ao contexto:`, err);
          }
        }
      }
      
      // Chamar a callback de salvamento
      onSave();
      
      toastSuccess(i18n.t("ticketCard.finalized"));
      setFinalizing(false);
      onClose();
    } catch (err) {
      setFinalizing(false);
      toastError(err);
    }
  };
  
  const toggleMessageSelection = (message) => {
    if (selectedMessages.some(m => m.id === message.id)) {
      setSelectedMessages(selectedMessages.filter(m => m.id !== message.id));
    } else {
      setSelectedMessages([...selectedMessages, message]);
    }
  };
  
  const handleManageMessages = () => {
    // Toggle includeMessagesInContext para mostrar/esconder a seção de seleção de mensagens
    setIncludeMessagesInContext(!includeMessagesInContext);
    if (!includeMessagesInContext) {
      setSelectedMessages([]);
    }
  };
  
  if (loading || cardLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <div className={classes.progress}>
            <CircularProgress />
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  const getPriorityLabel = (priority) => {
    switch (priority) {
      case "low":
        return i18n.t("ticketCard.panel.priorityOptions.low");
      case "high":
        return i18n.t("ticketCard.panel.priorityOptions.high");
      case "medium":
      default:
        return i18n.t("ticketCard.panel.priorityOptions.medium");
    }
  };
  
  const getPriorityClass = (priority) => {
    switch (priority) {
      case "low":
        return classes.priorityLow;
      case "high":
        return classes.priorityHigh;
      case "medium":
      default:
        return classes.priorityMedium;
    }
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      aria-labelledby="finalize-ticket-title"
    >
      <DialogTitle id="finalize-ticket-title">
        {i18n.t("ticketCard.finalizeTicket.title")}
      </DialogTitle>
      
      <DialogContent className={classes.dialogContent}>
        <Typography variant="subtitle1" gutterBottom>
          {i18n.t("ticketCard.finalizeTicket.subtitle")}
        </Typography>
        
        {finalStage && (
          <Paper className={classes.finalStageInfo} elevation={0}>
            <ArrowForwardIcon className={classes.icon} />
            <Typography variant="body2">
              {i18n.t("ticketCard.finalizeTicket.moveToFinalStage", { stage: finalStage.name })}
            </Typography>
          </Paper>
        )}
        
        <Divider className={classes.divider} />
        
        <Typography variant="h6" className={classes.title}>
          {i18n.t("ticketCard.finalizeTicket.cardInfo")}
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label={i18n.t("ticketCard.panel.title")}
              name="title"
              value={formData.title}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              className={classes.formControl}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label={i18n.t("ticketCard.panel.description")}
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              className={classes.formControl}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl 
              variant="outlined" 
              fullWidth 
              size="small"
              className={classes.formControl}
            >
              <InputLabel>{i18n.t("ticketCard.panel.priority")}</InputLabel>
              <Select
                name="priority"
                value={formData.priority || "medium"}
                onChange={handleChange}
                label={i18n.t("ticketCard.panel.priority")}
              >
                <MenuItem value="low">
                  {i18n.t("ticketCard.panel.priorityOptions.low")}
                </MenuItem>
                <MenuItem value="medium">
                  {i18n.t("ticketCard.panel.priorityOptions.medium")}
                </MenuItem>
                <MenuItem value="high">
                  {i18n.t("ticketCard.panel.priorityOptions.high")}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              label={i18n.t("ticketCard.panel.tags")}
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              className={classes.formControl}
              placeholder="tag1, tag2, tag3"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
              <DatePicker
                disableToolbar
                variant="inline"
                inputVariant="outlined"
                format="dd/MM/yyyy"
                margin="normal"
                label={i18n.t("ticketCard.panel.dueDate")}
                value={formData.dueDate}
                onChange={handleDateChange}
                fullWidth
                size="small"
                clearable
                autoOk
              />
            </MuiPickersUtilsProvider>
          </Grid>
        </Grid>
        
        <Divider className={classes.divider} />
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Button
              variant="outlined"
              color={includeMessagesInContext ? "secondary" : "primary"}
              onClick={handleManageMessages}
              startIcon={<MessageIcon />}
              className={classes.selectMessagesButton}
            >
              {includeMessagesInContext 
                ? i18n.t("ticketCard.finalizeTicket.doNotIncludeMessages") 
                : i18n.t("ticketCard.finalizeTicket.includeMessages")}
            </Button>
          </Grid>
          
          {includeMessagesInContext && selectedMessages.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>
                {i18n.t("ticketCard.finalizeTicket.selectedMessages", { count: selectedMessages.length })}
              </Typography>
              
              <Paper className={classes.messagePreview} variant="outlined">
                {selectedMessages.slice(0, 3).map((message) => (
                  <div key={message.id}>
                    <div className={classes.messageHeader}>
                      <Typography variant="body2" component="span">
                        {message.fromMe 
                          ? i18n.t("ticketCard.finalizeTicket.you") 
                          : ticket.contact?.name || i18n.t("ticketCard.finalizeTicket.contact")}
                      </Typography>
                      <Typography variant="caption" className={classes.messageTimestamp}>
                        {format(parseISO(message.createdAt), "dd/MM/yyyy HH:mm")}
                      </Typography>
                    </div>
                    <Typography variant="body2" className={classes.messageText}>
                      {message.body.length > 100 
                        ? `${message.body.substring(0, 100)}...` 
                        : message.body}
                    </Typography>
                    <Divider className={classes.divider} />
                  </div>
                ))}
                
                {selectedMessages.length > 3 && (
                  <Typography variant="body2">
                    {i18n.t("ticketCard.finalizeTicket.moreMessages", { count: selectedMessages.length - 3 })}
                  </Typography>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          {i18n.t("ticketCard.finalizeTicket.cancel")}
        </Button>
        <Button 
          onClick={handleSave} 
          color="primary" 
          variant="contained" 
          disabled={finalizing}
          startIcon={finalizing ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {i18n.t("ticketCard.finalizeTicket.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinalizeTicketModal;